import useTypedTranslation from '@/hooks/useTypedTranslation';
import useBrowseMenu from '@/hooks/useBrowseMenu';
import { IndexV2Enum } from '@/types/translations/IndexV2Enum';
import { IndexNavNewProps } from '@/types';
import MegaMenuDropdown from '@/components/common/navigation/MegaMenuDropdown';
import SellMegaMenuDropdown from '@/components/common/navigation/SellMegaMenuDropdown';
import { MegaMenuSection } from '@/types/mega-menu';
import Link from 'next/link';
import { IoChevronDownSharp } from 'react-icons/io5';
import { useMemo, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import getNewSkus from '@/api/getNewSkus';
import getCollectionsUseQuery from '@/api/getCollectionsUseQuery';
import getReleaseCalendar from '@/api/getReleaseCalendar';
import { useRouter } from 'next/router';
import checkArray from '@/utilites/check-array';
import { SkuDTO } from '@/types/api/sku';
import {
  createMostPopularTopicUrl,
  createNewestTopicUrl,
  createReleaseCalendarTopicUrl,
  createSearchUrl,
} from '@/utilites/search/create-search-urls';
import { resolveBrowseMenuLabel } from '@/constants/ui-items/browseMenu.constants';
import { HeaderEnum } from '@/types/translations/HeaderEnum';
import Image from '@/components/common/SpiritoryImage';
import MagazineMenuImage from '@/public/images/menu/magazine.jpg';
import NewsMenuImage from '@/public/images/menu/news.jpg';

const brandMegaMenuLabelMap = new Map<string, IndexV2Enum>([
  ['Top Brands', IndexV2Enum['home_nav.top_brands']],
  ['Trending Brands', IndexV2Enum['home_nav.trending_brands']],
  ['Bourbon & American', IndexV2Enum['home_nav.bourbon_american']],
  ['Spirits & Rum', IndexV2Enum['home_nav.spirits_rum']],
  ['Show all Brands', IndexV2Enum['home_nav.show_all_brands']],
]);

const countriesMegaMenuLabelMap = new Map<string, IndexV2Enum>([
  ['Scotland', IndexV2Enum['home_nav.country_scotland']],
  ['Europe', IndexV2Enum['home_nav.europe']],
  ['Asia', IndexV2Enum['home_nav.asia']],
  ['America & Caribbean', IndexV2Enum['home_nav.america_caribbean']],
  ['Speyside', IndexV2Enum['home_nav.region_speyside']],
  ['Islay', IndexV2Enum['home_nav.region_islay']],
  ['Highland', IndexV2Enum['home_nav.region_highland']],
  ['Lowland', IndexV2Enum['home_nav.region_lowland']],
  ['Campbeltown', IndexV2Enum['home_nav.region_campbeltown']],
  ['Island', IndexV2Enum['home_nav.region_island']],
  ['Ireland', IndexV2Enum['home_nav.country_ireland']],
  ['England', IndexV2Enum['home_nav.country_england']],
  ['Germany', IndexV2Enum['home_nav.country_germany']],
  ['France', IndexV2Enum['home_nav.country_france']],
  ['Spain', IndexV2Enum['home_nav.country_spain']],
  ['Italy', IndexV2Enum['home_nav.country_italy']],
  ['Nordics', IndexV2Enum['home_nav.country_nordics']],
  ['Japan', IndexV2Enum['home_nav.country_japan']],
  ['India', IndexV2Enum['home_nav.country_india']],
  ['Taiwan', IndexV2Enum['home_nav.country_taiwan']],
  ['China', IndexV2Enum['home_nav.country_china']],
  ['Korea', IndexV2Enum['home_nav.country_korea']],
  ['United States', IndexV2Enum['home_nav.country_united_states']],
  ['Canada', IndexV2Enum['home_nav.country_canada']],
  ['Mexico', IndexV2Enum['home_nav.country_mexico']],
  ['Jamaica', IndexV2Enum['home_nav.country_jamaica']],
  ['Guyana', IndexV2Enum['home_nav.country_guyana']],
  ['Barbados', IndexV2Enum['home_nav.country_barbados']],
]);

function translateMappedLabel(
  label: string,
  translate: (key: IndexV2Enum) => string,
  mapping: Map<string, IndexV2Enum>
) {
  const key = mapping.get(label);
  return key ? translate(key) : label;
}

export function IndexNavAll() {
  const { translate } = useTypedTranslation<IndexV2Enum>('index-v2');
  const { translate: translateHeader } = useTypedTranslation<HeaderEnum>('header');
  const { browseMenu, handleBrowseMenuItemClick } = useBrowseMenu();
  const sections = useMemo<MegaMenuSection[]>(
    () =>
      browseMenu.desktop.map(section => ({
        id: section.id,
        title: resolveBrowseMenuLabel(section.title, translateHeader),
        items: section.items.map(item => ({
          id: item.id,
          label: resolveBrowseMenuLabel(item.label, translateHeader),
          href: item.href,
          spacedAbove: item.spacedAbove,
          onClick: () => handleBrowseMenuItemClick(item),
        })),
      })),
    [browseMenu.desktop, handleBrowseMenuItemClick, translateHeader]
  );

  return (
    <MegaMenuDropdown
      trigger={
        <>
          {translate(IndexV2Enum['home_nav.browse'])}
          <IoChevronDownSharp className="tw-text-base tw-font-medium tw-text-textBlack" />
        </>
      }
      sections={sections}
      triggerClassName="tw-gap-1 tw-text-base tw-font-medium tw-text-textBlack"
      contentClassName="tw-fixed tw-inset-x-0 tw-top-[var(--index-nav-bottom,160px)] tw-px-4"
      containerClassName="tw-max-w-[1312px]"
      panelClassName="tw-rounded-[36px] tw-px-10 tw-py-8"
      titleClassName="tw-font-bold"
      layoutClassName="tw-gap-10"
    />
  );
}

export function IndexNavSellNow() {
  const { translate } = useTypedTranslation<IndexV2Enum>('index-v2');

  return (
    <SellMegaMenuDropdown
      trigger={
        <>
          {translate(IndexV2Enum['home_nav.sell_now'])}
          <IoChevronDownSharp className="tw-text-base tw-font-medium tw-text-green-600" />
        </>
      }
      triggerClassName="tw-gap-1 tw-text-base tw-font-medium tw-text-green-600"
      contentClassName="tw-fixed tw-inset-x-0 tw-top-[var(--index-nav-bottom,160px)] tw-px-4"
      containerClassName="tw-max-w-[1312px]"
      panelClassName="tw-rounded-[36px] tw-px-10 tw-py-8"
      layoutClassName="tw-gap-10"
      sectionClassName="tw-min-w-0"
      titleClassName="tw-font-bold"
      descriptionClassName="tw-max-w-[320px] tw-text-sm tw-font-medium tw-leading-6 tw-text-textBlack"
      footerLinkClassName="tw-mt-5 tw-text-sm tw-font-medium tw-text-textBlack tw-underline tw-underline-offset-2 hover:tw-text-main"
    />
  );
}

export function IndexNavMagazineNews() {
  const { translate } = useTypedTranslation<IndexV2Enum>('index-v2');

  const cards = [
    {
      id: 'magazine',
      title: translate(IndexV2Enum['home_nav.magazine_card_title']),
      description: translate(IndexV2Enum['home_nav.magazine_card_description']),
      ctaLabel: translate(IndexV2Enum['home_nav.magazine_card_cta']),
      href: '/magazine',
      image: MagazineMenuImage,
      imageAlt: translate(IndexV2Enum['home_nav.magazine_card_image_alt']),
    },
    {
      id: 'news',
      title: translate(IndexV2Enum['home_nav.news_card_title']),
      description: translate(IndexV2Enum['home_nav.news_card_description']),
      ctaLabel: translate(IndexV2Enum['home_nav.news_card_cta']),
      href: '/news',
      image: NewsMenuImage,
      imageAlt: translate(IndexV2Enum['home_nav.news_card_image_alt']),
    },
  ];

  return (
    <MegaMenuDropdown
      trigger={
        <>
          {translate(IndexV2Enum['home_nav.magazine_and_news'])}
          <IoChevronDownSharp className="tw-text-base tw-font-medium tw-text-textBlack" />
        </>
      }
      sections={[]}
      triggerClassName="tw-gap-1 tw-text-base tw-font-medium tw-text-textBlack"
      contentClassName="tw-fixed tw-inset-x-0 tw-top-[var(--index-nav-bottom,160px)] tw-px-4"
      containerClassName="tw-max-w-[1312px]"
      panelClassName="tw-rounded-[36px] tw-px-10 tw-py-8"
      layoutClassName="tw-gap-10"
      trailingContent={
        <div className="tw-grid tw-w-full tw-grid-cols-2 tw-gap-10 max-nine:tw-grid-cols-1 max-nine:tw-gap-8">
          {cards.map(card => (
            <article
              key={card.id}
              className="tw-grid tw-min-w-0 tw-grid-cols-[1fr_180px] tw-items-start tw-gap-8"
            >
              <div className="tw-min-w-0">
                <h3 className="tw-mb-3 tw-whitespace-nowrap tw-px-2 tw-text-base tw-font-bold tw-text-textBlack">
                  {card.title}
                </h3>
                <p className="tw-mb-4 tw-max-w-[320px] tw-px-2 tw-text-sm tw-font-medium tw-leading-6 tw-text-textBlack">
                  {card.description}
                </p>
                <Link
                  href={card.href}
                  className="tw-mt-5 tw-inline-block tw-px-2 tw-text-sm tw-font-medium tw-text-textBlack tw-underline tw-underline-offset-2 hover:tw-text-main"
                >
                  {card.ctaLabel}
                </Link>
              </div>

              <Link
                href={card.href}
                className="tw-relative tw-block tw-h-[180px] tw-w-[180px] tw-flex-none tw-overflow-hidden tw-rounded-[20px]"
              >
                <Image src={card.image} alt={card.imageAlt} fill className="tw-object-cover" />
              </Link>
            </article>
          ))}
        </div>
      }
    />
  );
}

interface IndexNavBrandsProps {
  indexMegaMenuBrands: MegaMenuSection[];
}
export function IndexNavBrands({ indexMegaMenuBrands }: IndexNavBrandsProps) {
  const { translate } = useTypedTranslation<IndexV2Enum>('index-v2');
  const sections = useMemo(
    () =>
      indexMegaMenuBrands.map(section => ({
        ...section,
        title: translateMappedLabel(section.title, translate, brandMegaMenuLabelMap),
        footerLink: section.footerLink
          ? {
              ...section.footerLink,
              label: translateMappedLabel(
                section.footerLink.label,
                translate,
                brandMegaMenuLabelMap
              ),
            }
          : undefined,
      })),
    [indexMegaMenuBrands, translate]
  );
  const brandOfTheMonth = useMemo(
    () =>
      sections.flatMap(section => section.items).find(item => item.label === 'Ardbeg') ?? {
        label: 'Ardbeg',
        href: '/brands/Ardbeg',
        onClick: () => {},
      },
    [sections]
  );

  return (
    <MegaMenuDropdown
      trigger={
        <>
          {translate(IndexV2Enum['home_nav.brand'])}
          <IoChevronDownSharp className="tw-text-base tw-font-medium tw-text-textBlack" />
        </>
      }
      sections={sections}
      triggerClassName="tw-gap-1 tw-text-base tw-font-medium tw-text-textBlack"
      contentClassName="tw-fixed tw-inset-x-0 tw-top-[var(--index-nav-bottom,160px)] tw-px-4"
      containerClassName="tw-max-w-[1312px]"
      panelClassName="tw-rounded-[36px] tw-px-10 tw-py-8"
      titleClassName="tw-font-bold"
      layoutClassName="tw-gap-10"
      trailingContent={
        brandOfTheMonth ? (
          <aside className="tw-w-[220px] tw-flex-shrink-0">
            <div className="tw-mb-4 tw-text-base tw-font-bold tw-text-textBlack">
              {translate(IndexV2Enum['home_nav.brand_of_the_month'])}
            </div>
            <div className="tw-relative tw-flex tw-min-h-[340px] tw-flex-col tw-justify-end tw-overflow-hidden tw-rounded-[28px] tw-p-5 tw-shadow-[0_18px_40px_rgba(0,0,0,0.25)]">
              <Image
                src="https://dataspiritory.b-cdn.net/banner/navigation-banner/Ardbeg%20Brand%20-%20Navbar.png"
                alt="Brand of the Month"
                fill
                className="tw-object-cover"
              />
              <div className="tw-relative tw-z-[1] tw-mt-auto tw-space-y-4">
                <div className="tw-text-center tw-text-xl tw-font-bold tw-text-white">
                  {brandOfTheMonth.label}
                </div>
                <Link
                  href={brandOfTheMonth.href}
                  onClick={brandOfTheMonth.onClick}
                  className="tw-flex tw-h-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-main tw-text-sm tw-font-semibold tw-text-white tw-no-underline hover:tw-opacity-90"
                >
                  {translate(IndexV2Enum['home_nav.shop_now'])}
                </Link>
              </div>
            </div>
          </aside>
        ) : null
      }
    />
  );
}

export function IndexNavNew(_props: IndexNavNewProps) {
  const { translate } = useTypedTranslation<IndexV2Enum>('index-v2');
  const router = useRouter();

  const prepareQueryData = useCallback(<T,>(data: any): T[] => {
    return (checkArray(data) ? data : []) as T[];
  }, []);

  const QUERY_CONFIG = {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  };

  const [collectionsQuery, newSkusQuery, releaseCalendarQuery] = useQueries({
    queries: [
      {
        ...QUERY_CONFIG,
        queryKey: ['collections', 'all', router.locale],
        queryFn: getCollectionsUseQuery,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['newSkus'],
        queryFn: getNewSkus,
        select: (data: any) => prepareQueryData<SkuDTO>(data),
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['releaseCalendar'],
        queryFn: getReleaseCalendar,
        select: (data: any) => prepareQueryData<SkuDTO>(data),
      },
    ],
  });

  const newCollections = useMemo(() => {
    const collections = collectionsQuery.data || [];
    const _newCollections = collections.sort((a, b) => {
      const dateA = new Date(a.createdAt || '');
      const dateB = new Date(b.createdAt || '');
      return dateB.getTime() - dateA.getTime();
    });
    return _newCollections.slice(0, 5).map(collection => ({
      name: collection?.name || '',
      href: `/collections/${collection?.slug}`,
    }));
  }, [collectionsQuery.data]);

  const newSkus = useMemo(() => {
    const skus = newSkusQuery.data || [];
    return skus.slice(0, 5).map(sku => ({
      name: sku?.name || '',
      href: `/${sku.slug}`,
    }));
  }, [newSkusQuery.data]);

  const upcomingReleases = releaseCalendarQuery.data || [];

  const sections = useMemo<MegaMenuSection[]>(
    () => [
      {
        id: 'new-bottles',
        title: translate(IndexV2Enum['home_nav.new_bottles']),
        items: newSkus.map((sku, index) => ({
          id: `new-bottle-${index}-${sku.href}`,
          label: sku.name,
          href: sku.href,
        })),
        footerLink: {
          id: 'show-all-new-bottles',
          label: translate(IndexV2Enum['home_nav.show_all_new_bottles']),
          href: createNewestTopicUrl(),
        },
      },
      {
        id: 'upcoming-releases',
        title: translate(IndexV2Enum['home_nav.upcoming_releases']),
        items: upcomingReleases.slice(0, 7).map((sku, index) => ({
          id: `upcoming-release-${index}-${sku.id}`,
          label: sku.name || '',
          href: `/${sku.slug}`,
        })),
        footerLink: {
          id: 'show-all-upcoming-releases',
          label: translate(IndexV2Enum['home_nav.show_all_upcoming_releases']),
          href: createReleaseCalendarTopicUrl(),
        },
      },
      {
        id: 'new-collections',
        title: translate(IndexV2Enum['home_nav.new_collections']),
        items: newCollections.map((collection, index) => ({
          id: `new-collection-${index}-${collection.href}`,
          label: collection.name,
          href: collection.href,
        })),
      },
      {
        id: 'explore-spiritory',
        title: translate(IndexV2Enum['home_nav.explore_spiritory']),
        items: [
          {
            id: 'explore-customer-favorites',
            label: translate(IndexV2Enum['home_nav.customer_favorites']),
            href: createMostPopularTopicUrl(),
          },
          {
            id: 'explore-rare-collectible',
            label: translate(IndexV2Enum['home_nav.rare_collectible']),
            href: createSearchUrl('Rare Collectible'),
          },
          {
            id: 'explore-limited-editions',
            label: translate(IndexV2Enum['home_nav.limited_edition']),
            href: createSearchUrl('Limited Edition'),
          },
          {
            id: 'explore-gift-ideas',
            label: translate(IndexV2Enum['home_nav.gift_ideas']),
            href: createSearchUrl('Gift Ideas'),
          },
        ],
      },
    ],
    [newCollections, newSkus, upcomingReleases, translate]
  );

  return (
    <MegaMenuDropdown
      trigger={
        <>
          {translate(IndexV2Enum['home_nav.new'])}
          <IoChevronDownSharp className="tw-text-base tw-font-medium tw-text-textBlack" />
        </>
      }
      sections={sections}
      triggerClassName="tw-gap-1 tw-text-base tw-font-medium tw-text-textBlack"
      contentClassName="tw-fixed tw-inset-x-0 tw-top-[var(--index-nav-bottom,160px)] tw-px-4"
      containerClassName="tw-max-w-[1312px]"
      panelClassName="tw-rounded-[36px] tw-px-10 tw-py-8"
      titleClassName="tw-font-bold"
      layoutClassName="tw-gap-10"
      sectionClassName="tw-min-w-0"
      itemClassName="tw-max-w-[220px] tw-overflow-hidden tw-text-ellipsis"
    />
  );
}

interface IndexNavCountriesProps {
  indexMegaMenuCountries: MegaMenuSection[];
}
export function IndexNavCountries({ indexMegaMenuCountries }: IndexNavCountriesProps) {
  const { translate } = useTypedTranslation<IndexV2Enum>('index-v2');
  const sections = useMemo(
    () =>
      indexMegaMenuCountries.map(section => ({
        ...section,
        title: translateMappedLabel(section.title, translate, countriesMegaMenuLabelMap),
        items: section.items.map(item => ({
          ...item,
          label: translateMappedLabel(item.label, translate, countriesMegaMenuLabelMap),
        })),
      })),
    [indexMegaMenuCountries, translate]
  );

  return (
    <MegaMenuDropdown
      trigger={
        <>
          {translate(IndexV2Enum['home_nav.countries'])}
          <IoChevronDownSharp className="tw-text-base tw-font-medium tw-text-textBlack" />
        </>
      }
      sections={sections}
      triggerClassName="tw-gap-1 tw-text-base tw-font-medium tw-text-textBlack"
      contentClassName="tw-fixed tw-inset-x-0 tw-top-[var(--index-nav-bottom,160px)] tw-px-4"
      containerClassName="tw-max-w-[1312px]"
      panelClassName="tw-rounded-[36px] tw-px-10 tw-py-8"
      titleClassName="tw-font-bold"
      layoutClassName="tw-gap-10"
    />
  );
}
